/**
 *  版权所有 © 2026 东方闻道 All Rights Reserved
 *
 *  创建者: admin
 *  创建时间: 2026-09-20 15:47
 *  文件名: src/components/ppt-learning-plan-viewer/word-editor/index.tsx
 *  功能描述: Word 在线编辑组件
 */
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { css } from 'emotion';
import env_api from '../../../config/api';
import { listenOfficeEditorChannel } from './office-editor-channel.handlers';
import {
  DocumentForceSaveResult,
  DocumentSyncResult,
  forceSaveOfficeEditorDocument,
  gotoOfficeEditorBookmark,
  selectOfficeEditorBookmark,
  waitOfficeEditorDocumentSync,
} from './office-editor-channel.methods';
import { APP_CODE } from '../../../constant/common';
import { getUserId } from '../../../util';
import { LearningPlanPreviewContext } from '../types';
import { getPreviewResources } from '../../../redux/action/lesson.action';

const styles = {
  wrapper: css`
    position: relative;
    width: 100%;
    height: 100%;
  `,
  iframe: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  `,
};

interface WordEditorProps {
  fileId: string;
  previewContext: LearningPlanPreviewContext;
}

interface WordEditorController {
  gotoBookmark(bookmarkName: string): void;
  selectBookmark(bookmarkName: string): void;
  waitDocumentSync(): Promise<DocumentSyncResult>;
  forceSave(): Promise<DocumentForceSaveResult>;
}

/** 构建 Word 编辑器地址。 */
function buildEditorUrl(fileId: string, fileName: string | undefined, bid: string): string {
  const searchParams = new URLSearchParams();
  searchParams.set('appId', APP_CODE);
  searchParams.set('sourceFileId', fileId);
  searchParams.set('bid', bid);
  searchParams.set('userId', getUserId());
  if (fileName) {
    searchParams.set('fileName', fileName);
  }
  return `${env_api.officeEditorService}/edit?${searchParams.toString()}`;
}

/** 嵌入 EuroOffice 编辑页并响应编辑器的认证请求。 */
const WordEditor = forwardRef<WordEditorController, WordEditorProps>((props, ref) => {
  const { fileId, previewContext } = props;
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const editorUrl = useMemo(
    () =>
      buildEditorUrl(
        previewContext.selfFile.fileId ?? fileId, // 先使用用户自己修改过的文件
        previewContext.wordFileName,
        previewContext.selfFile.bid,
      ),
    [fileId],
  );
  const editorOrigin = useMemo(() => new URL(editorUrl).origin, [editorUrl]);

  // 在线编辑器不经过资源预览接口，需要单独调用以记录 Word 预览埋点。
  useEffect(() => {
    getPreviewResources(
      previewContext.selfFile.fileId ?? fileId,
      previewContext.type,
      encodeURIComponent(previewContext.wordFileName),
      previewContext,
    );
  }, [
    fileId,
    previewContext.selfFile.fileId,
    previewContext.type,
    previewContext.wordFileName,
    previewContext.resourceId,
    previewContext.courseId,
    previewContext.lessonId,
    previewContext.originalFileId,
    previewContext.documentId,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      gotoBookmark(bookmarkName: string) {
        const editorWindow = iframeRef.current && iframeRef.current.contentWindow;
        if (!editorWindow) return;

        gotoOfficeEditorBookmark({ editorWindow, editorOrigin }, bookmarkName);
      },
      selectBookmark(bookmarkName: string) {
        const editorWindow = iframeRef.current && iframeRef.current.contentWindow;
        if (!editorWindow) return;

        selectOfficeEditorBookmark({ editorWindow, editorOrigin }, bookmarkName);
      },
      waitDocumentSync() {
        const editorWindow = iframeRef.current && iframeRef.current.contentWindow;
        if (!editorWindow) return Promise.reject(new Error('EuroOffice 编辑器尚未就绪'));

        return waitOfficeEditorDocumentSync({ editorWindow, editorOrigin });
      },
      forceSave() {
        const editorWindow = iframeRef.current && iframeRef.current.contentWindow;
        if (!editorWindow) return Promise.reject(new Error('EuroOffice 编辑器尚未就绪'));

        return forceSaveOfficeEditorDocument({ editorWindow, editorOrigin });
      },
    }),
    [editorOrigin],
  );

  useEffect(() => {
    const editorWindow = iframeRef.current && iframeRef.current.contentWindow;
    if (!editorWindow) return;

    return listenOfficeEditorChannel({ editorWindow, editorOrigin });
  }, [editorOrigin]);

  return (
    <div className={styles.wrapper}>
      <iframe
        ref={iframeRef}
        src={editorUrl}
        className={styles.iframe}
        title={previewContext.wordFileName || '在线编辑'}
      />
    </div>
  );
});

WordEditor.displayName = 'WordEditor';

export { WordEditor, buildEditorUrl };
export type { WordEditorController, WordEditorProps };
